if (!customElements.get('dispatch-countdown')) {
  class DispatchCountdown extends HTMLElement {
    connectedCallback() {
      if (this.isConnectedToTimer) return;

      this.isConnectedToTimer = true;

      this.eyebrowElement = this.querySelector('[data-dispatch-eyebrow]');
      this.headingElement = this.querySelector('[data-dispatch-heading]');
      this.messageElement = this.querySelector('[data-dispatch-message]');
      this.timerElement = this.querySelector('[data-dispatch-timer]');

      this.hoursElement = this.querySelector('[data-dispatch-hours]');
      this.minutesElement = this.querySelector('[data-dispatch-minutes]');
      this.secondsElement = this.querySelector('[data-dispatch-seconds]');
      this.secondsGroup = this.querySelector(
        '[data-dispatch-seconds-group]'
      );

      this.timeZone = this.dataset.timeZone || 'Europe/London';

      this.cutoffHour = this.parseNumber(
        this.dataset.cutoffHour,
        13
      );

      this.cutoffMinute = this.parseNumber(
        this.dataset.cutoffMinute,
        30
      );

      this.warningMinutes = this.parseNumber(
        this.dataset.warningMinutes,
        30
      );

      this.finalWarningMinutes = this.parseNumber(
        this.dataset.finalWarningMinutes,
        5
      );

      this.showSeconds = this.dataset.showSeconds !== 'false';

      this.afterCutoffMessage =
        this.dataset.afterCutoffMessage ||
        'Order now and we’ll prepare it for the next working day.';

      this.weekendMessage =
        this.dataset.weekendMessage ||
        'We’ll dispatch your order first thing Monday.';

      if (this.secondsGroup) {
        this.secondsGroup.hidden = !this.showSeconds;
      }

      this.update();
      this.timerInterval = window.setInterval(() => this.update(), 1000);
    }

    disconnectedCallback() {
      if (this.timerInterval) {
        window.clearInterval(this.timerInterval);
      }

      this.isConnectedToTimer = false;
    }

    parseNumber(value, fallback) {
      const parsed = Number.parseInt(value, 10);
      return Number.isFinite(parsed) ? parsed : fallback;
    }

    getLondonTime() {
      const formatter = new Intl.DateTimeFormat('en-GB', {
        timeZone: this.timeZone,
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23'
      });

      const formattedParts = formatter.formatToParts(new Date());
      const values = {};

      formattedParts.forEach((part) => {
        if (part.type !== 'literal') {
          values[part.type] = part.value;
        }
      });

      return {
        weekday: values.weekday,
        hour: Number.parseInt(values.hour, 10),
        minute: Number.parseInt(values.minute, 10),
        second: Number.parseInt(values.second, 10)
      };
    }

    getRemainingSeconds(time) {
      const currentSeconds =
        time.hour * 3600 +
        time.minute * 60 +
        time.second;

      const cutoffSeconds =
        this.cutoffHour * 3600 +
        this.cutoffMinute * 60;

      return cutoffSeconds - currentSeconds;
    }

    updateTimer(totalSeconds) {
      const remaining = Math.max(totalSeconds, 0);

      const hours = Math.floor(remaining / 3600);
      const minutes = Math.floor((remaining % 3600) / 60);
      const seconds = remaining % 60;

      if (this.hoursElement) {
        this.hoursElement.textContent = String(hours).padStart(2, '0');
      }

      if (this.minutesElement) {
        this.minutesElement.textContent = String(minutes).padStart(2, '0');
      }

      if (this.secondsElement) {
        this.secondsElement.textContent = String(seconds).padStart(2, '0');
      }
    }

    setContent({
      state,
      eyebrow,
      heading,
      message,
      showTimer = false
    }) {
      this.dataset.state = state;

      if (this.eyebrowElement) {
        this.eyebrowElement.textContent = eyebrow;
      }

      if (this.headingElement) {
        this.headingElement.textContent = heading;
      }

      if (this.messageElement) {
        this.messageElement.textContent = message;
      }

      if (this.timerElement) {
        this.timerElement.hidden = !showTimer;
      }
    }

    update() {
      const time = this.getLondonTime();
      const remainingSeconds = this.getRemainingSeconds(time);
      const remainingMinutes = Math.ceil(remainingSeconds / 60);

      const isSaturday = time.weekday === 'Sat';
      const isSunday = time.weekday === 'Sun';
      const isFriday = time.weekday === 'Fri';
      const isWeekend = isSaturday || isSunday;

      if (isWeekend) {
        this.setContent({
          state: 'weekend',
          eyebrow: 'Enjoy your weekend',
          heading: 'Next dispatch: Monday',
          message: this.weekendMessage
        });

        return;
      }

      if (remainingSeconds > 0) {
        this.updateTimer(remainingSeconds);

        if (remainingMinutes <= this.finalWarningMinutes) {
          this.setContent({
            state: 'final-warning',
            eyebrow: 'Last chance',
            heading: 'You’re one of today’s final orders',
            message: 'Complete your order before dispatch closes.',
            showTimer: true
          });

          return;
        }

        if (remainingMinutes <= this.warningMinutes) {
          this.setContent({
            state: 'warning',
            eyebrow: 'You’re just in time',
            heading: 'Order now for dispatch today',
            message: 'Only a short time remains before today’s cut-off.',
            showTimer: true
          });

          return;
        }

        this.setContent({
          state: 'available',
          eyebrow: 'Dispatches today',
          heading: 'Order within',
          message: 'to leave our warehouse today.',
          showTimer: true
        });

        return;
      }

      if (isFriday) {
        this.setContent({
          state: 'closed',
          eyebrow: 'Today’s cut-off has passed',
          heading: 'Next dispatch: Monday',
          message: this.afterCutoffMessage
        });

        return;
      }

      this.setContent({
        state: 'closed',
        eyebrow: 'Today’s cut-off has passed',
        heading: 'Next dispatch: Tomorrow',
        message: this.afterCutoffMessage
      });
    }
  }

  customElements.define('dispatch-countdown', DispatchCountdown);
}